import React from 'react';
import {
    Alert,
    Autocomplete,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControl,
    FormHelperText,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import AppDatePicker from '../../shared/AppDatePicker';
import {
    CUSTOMER_SELECT_EMPTY_HELPER,
    CUSTOMER_SELECT_LABEL,
    CUSTOMER_SELECT_LOADING,
    CUSTOMER_SELECT_NO_OPTIONS,
    CUSTOMER_SELECT_PLACEHOLDER,
    getCustomerOptionLabel,
    isCustomerOptionEqualToValue,
    resolveCustomerOption,
} from '../../shared/customerSelectUtils';
import {
    SUPPLIER_SELECT_EMPTY_HELPER,
    SUPPLIER_SELECT_LABEL,
    SUPPLIER_SELECT_LOADING,
    SUPPLIER_SELECT_NO_OPTIONS,
    SUPPLIER_SELECT_PLACEHOLDER,
    getSupplierOptionLabel,
    isSupplierOptionEqualToValue,
    resolveSupplierOption,
} from '../../shared/supplierSelectUtils';
import {
    ALLOCATION_TYPES,
    formatAmount,
    formatDate,
    renderTargetOptions,
} from './utils';

const BankAssignDialog = ({
    open,
    onClose,
    onAssign,
    assigning,
    transaction,
    allocationRows,
    allocationErrors,
    assignError,
    referenceLoading,
    referenceData,
    customerOptions,
    supplierOptions,
    creatingCustomer,
    creatingSupplier,
    onAddRow,
    onRemoveRow,
    onAllocationTypeChange,
    onAllocationRowChange,
    onAllocationNestedChange,
    onCustomerSelection,
    onCustomerInput,
    onSupplierSelection,
    onSupplierInput,
    onCreateCustomer,
    onCreateSupplier,
    bankComment,
    onBankCommentChange,
}) => (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>Bank-Vorgang zuordnen</DialogTitle>
        <DialogContent dividers>
            {transaction && (
                <Stack spacing={2}>
                    <Box>
                        <Typography variant="subtitle2">
                            {transaction.counterpartName || 'Bank'}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            {`${formatDate(transaction.bookingDate || transaction.valueDate)} - ${formatAmount(transaction.amount, transaction.currency)}`}
                        </Typography>
                    </Box>

                    {assignError && (
                        <Alert severity="error">{assignError}</Alert>
                    )}

                    {referenceLoading ? (
                        <Typography color="text.secondary">Lade Zuordnungsdaten...</Typography>
                    ) : (
                        <Stack spacing={2}>
                            {allocationRows.map((row) => {
                                const rowErrors = allocationErrors[row.id] || {};
                                return (
                                    <Box key={row.id}>
                                        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="center">
                                            <FormControl size="small" sx={{ minWidth: 180 }}>
                                                <InputLabel id={`bank-alloc-type-${row.id}`}>Ziel</InputLabel>
                                                <Select
                                                    labelId={`bank-alloc-type-${row.id}`}
                                                    value={row.allocationType}
                                                    label="Ziel"
                                                    onChange={(event) =>
                                                        onAllocationTypeChange(row.id, event.target.value)
                                                    }
                                                >
                                                    {ALLOCATION_TYPES.map((item) => (
                                                        <MenuItem key={item.value} value={item.value}>
                                                            {item.label}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>

                                            <FormControl size="small" sx={{ minWidth: 240 }} error={Boolean(rowErrors.targetId)}>
                                                <InputLabel id={`bank-alloc-target-${row.id}`}>Auswahl</InputLabel>
                                                <Select
                                                    labelId={`bank-alloc-target-${row.id}`}
                                                    value={row.targetId}
                                                    label="Auswahl"
                                                    disabled={row.allocationType === 'NEW_INCOME'
                                                        || row.allocationType === 'NEW_EXPENSE'}
                                                    onChange={(event) => onAllocationRowChange(row.id, {
                                                        targetId: event.target.value,
                                                    })}
                                                >
                                                    {renderTargetOptions(row.allocationType, referenceData)}
                                                </Select>
                                                {rowErrors.targetId && (
                                                    <FormHelperText>{rowErrors.targetId}</FormHelperText>
                                                )}
                                            </FormControl>

                                            <TextField
                                                label="Betrag"
                                                size="small"
                                                value={row.amount}
                                                onChange={(event) =>
                                                    onAllocationRowChange(row.id, { amount: event.target.value })
                                                }
                                                error={Boolean(rowErrors.amount)}
                                                helperText={rowErrors.amount || ''}
                                                sx={{ width: 140 }}
                                            />

                                            <AppDatePicker
                                                label="Zahlungsdatum"
                                                size="small"
                                                value={row.paymentDate}
                                                onChange={(date) => onAllocationRowChange(row.id, {
                                                    paymentDate: date,
                                                })}
                                                sx={{ width: 170 }}
                                            />

                                            {allocationRows.length > 1 && (
                                                <Button color="inherit" onClick={() => onRemoveRow(row.id)}>
                                                    Entfernen
                                                </Button>
                                            )}
                                        </Stack>

                                        {(row.allocationType === 'NEW_INCOME'
                                            || row.allocationType === 'NEW_EXPENSE') && (
                                            <Box sx={{ mt: 2, pl: 1 }}>
                                                <Typography variant="subtitle2" gutterBottom>
                                                    Neue Daten
                                                </Typography>
                                                <Stack spacing={2} direction={{ xs: 'column', md: 'row' }}>
                                                    <AppDatePicker
                                                        label="Belegdatum"
                                                        size="small"
                                                        value={row.allocationType === 'NEW_INCOME'
                                                            ? row.newIncome.invoiceDate
                                                            : row.newExpense.invoiceDate}
                                                        onChange={(date) => onAllocationNestedChange(
                                                            row.id,
                                                            row.allocationType === 'NEW_INCOME'
                                                                ? 'newIncome'
                                                                : 'newExpense',
                                                            { invoiceDate: date }
                                                        )}
                                                        error={Boolean(rowErrors.invoiceDate)}
                                                        helperText={rowErrors.invoiceDate || ''}
                                                    />
                                                    <TextField
                                                        label="Belegnummer/Anzeigename"
                                                        size="small"
                                                        value={row.allocationType === 'NEW_INCOME'
                                                            ? row.newIncome.invoiceNumber
                                                            : row.newExpense.invoiceNumber}
                                                        onChange={(event) => onAllocationNestedChange(
                                                            row.id,
                                                            row.allocationType === 'NEW_INCOME'
                                                                ? 'newIncome'
                                                                : 'newExpense',
                                                            { invoiceNumber: event.target.value }
                                                        )}
                                                        error={Boolean(rowErrors.invoiceNumber)}
                                                        helperText={rowErrors.invoiceNumber || ''}
                                                    />
                                                    {row.allocationType === 'NEW_INCOME' ? (
                                                        <Box
                                                            sx={{
                                                                display: 'flex',
                                                                flexDirection: { xs: 'column', md: 'row' },
                                                                gap: 1,
                                                                alignItems: { xs: 'stretch', md: 'center' },
                                                                flex: 1,
                                                                minWidth: 240,
                                                            }}
                                                        >
                                                            <Autocomplete
                                                                size="small"
                                                                options={customerOptions}
                                                                value={resolveCustomerOption(
                                                                    customerOptions,
                                                                    row.newIncome.customerId,
                                                                    row.newIncome.customerId ? row.newIncome.customerName : ''
                                                                )}
                                                                inputValue={row.newIncome.customerSearch}
                                                                onChange={(event, value) =>
                                                                    onCustomerSelection(row.id, value)
                                                                }
                                                                onInputChange={(event, value, reason) =>
                                                                    onCustomerInput(row.id, value, reason)
                                                                }
                                                                getOptionLabel={getCustomerOptionLabel}
                                                                isOptionEqualToValue={isCustomerOptionEqualToValue}
                                                                loading={referenceLoading}
                                                                loadingText={CUSTOMER_SELECT_LOADING}
                                                                noOptionsText={CUSTOMER_SELECT_NO_OPTIONS}
                                                                renderInput={(params) => (
                                                                    <TextField
                                                                        {...params}
                                                                        label={CUSTOMER_SELECT_LABEL}
                                                                        placeholder={CUSTOMER_SELECT_PLACEHOLDER}
                                                                        size="small"
                                                                        error={Boolean(rowErrors.customerName)}
                                                                        helperText={
                                                                            rowErrors.customerName ||
                                                                            (!referenceLoading && customerOptions.length === 0
                                                                                ? CUSTOMER_SELECT_EMPTY_HELPER
                                                                                : '')
                                                                        }
                                                                    />
                                                                )}
                                                                sx={{ flex: 1, minWidth: 220 }}
                                                            />
                                                            <Button
                                                                variant="outlined"
                                                                size="small"
                                                                onClick={() => onCreateCustomer(row.id)}
                                                                disabled={creatingCustomer
                                                                    || !row.newIncome.customerSearch.trim()}
                                                            >
                                                                {creatingCustomer ? 'Anlegen...' : 'Kunde anlegen'}
                                                            </Button>
                                                        </Box>
                                                    ) : (
                                                        <Box
                                                            sx={{
                                                                display: 'flex',
                                                                flexDirection: { xs: 'column', md: 'row' },
                                                                gap: 1,
                                                                alignItems: { xs: 'stretch', md: 'center' },
                                                                flex: 1,
                                                                minWidth: 240,
                                                            }}
                                                        >
                                                            <Autocomplete
                                                                size="small"
                                                                options={supplierOptions}
                                                                value={resolveSupplierOption(
                                                                    supplierOptions,
                                                                    row.newExpense.supplierId,
                                                                    row.newExpense.supplierId ? row.newExpense.supplierName : ''
                                                                )}
                                                                inputValue={row.newExpense.supplierName}
                                                                onChange={(event, value) =>
                                                                    onSupplierSelection(row.id, value)
                                                                }
                                                                onInputChange={(event, value, reason) =>
                                                                    onSupplierInput(row.id, value, reason)
                                                                }
                                                                getOptionLabel={getSupplierOptionLabel}
                                                                isOptionEqualToValue={isSupplierOptionEqualToValue}
                                                                loading={referenceLoading}
                                                                loadingText={SUPPLIER_SELECT_LOADING}
                                                                noOptionsText={SUPPLIER_SELECT_NO_OPTIONS}
                                                                renderInput={(params) => (
                                                                    <TextField
                                                                        {...params}
                                                                        label={SUPPLIER_SELECT_LABEL}
                                                                        placeholder={SUPPLIER_SELECT_PLACEHOLDER}
                                                                        size="small"
                                                                        error={Boolean(rowErrors.supplierName)}
                                                                        helperText={
                                                                            rowErrors.supplierName ||
                                                                            (!referenceLoading && supplierOptions.length === 0
                                                                                ? SUPPLIER_SELECT_EMPTY_HELPER
                                                                                : '')
                                                                        }
                                                                    />
                                                                )}
                                                                sx={{ flex: 1, minWidth: 220 }}
                                                            />
                                                            <Button
                                                                variant="outlined"
                                                                size="small"
                                                                onClick={() => onCreateSupplier(row.id)}
                                                                disabled={creatingSupplier
                                                                    || !row.newExpense.supplierName.trim()}
                                                            >
                                                                {creatingSupplier ? 'Anlegen...' : 'Lieferant anlegen'}
                                                            </Button>
                                                        </Box>
                                                    )}
                                                </Stack>
                                                <Stack spacing={2} direction={{ xs: 'column', md: 'row' }} sx={{ mt: 2 }}>
                                                    <TextField
                                                        label="Brutto"
                                                        size="small"
                                                        value={row.allocationType === 'NEW_INCOME'
                                                            ? row.newIncome.grossAmount
                                                            : row.newExpense.grossAmount}
                                                        onChange={(event) => onAllocationNestedChange(
                                                            row.id,
                                                            row.allocationType === 'NEW_INCOME'
                                                                ? 'newIncome'
                                                                : 'newExpense',
                                                            { grossAmount: event.target.value }
                                                        )}
                                                        error={Boolean(rowErrors.grossAmount)}
                                                        helperText={rowErrors.grossAmount || ''}
                                                    />
                                                    <TextField
                                                        label="Netto"
                                                        size="small"
                                                        value={row.allocationType === 'NEW_INCOME'
                                                            ? row.newIncome.netAmount
                                                            : row.newExpense.netAmount}
                                                        onChange={(event) => onAllocationNestedChange(
                                                            row.id,
                                                            row.allocationType === 'NEW_INCOME'
                                                                ? 'newIncome'
                                                                : 'newExpense',
                                                            { netAmount: event.target.value }
                                                        )}
                                                        error={Boolean(rowErrors.netAmount)}
                                                        helperText={rowErrors.netAmount || ''}
                                                    />
                                                    <TextField
                                                        label="USt"
                                                        size="small"
                                                        value={row.allocationType === 'NEW_INCOME'
                                                            ? row.newIncome.vatAmount
                                                            : row.newExpense.vatAmount}
                                                        onChange={(event) => onAllocationNestedChange(
                                                            row.id,
                                                            row.allocationType === 'NEW_INCOME'
                                                                ? 'newIncome'
                                                                : 'newExpense',
                                                            { vatAmount: event.target.value }
                                                        )}
                                                        error={Boolean(rowErrors.vatAmount)}
                                                        helperText={rowErrors.vatAmount || ''}
                                                    />
                                                </Stack>
                                            </Box>
                                        )}

                                        <Divider sx={{ my: 2 }} />
                                    </Box>
                                );
                            })}
                            <TextField
                                label="Kommentar"
                                size="small"
                                multiline
                                minRows={2}
                                value={bankComment}
                                onChange={(event) => onBankCommentChange(event.target.value)}
                            />
                            <Button startIcon={<AddRoundedIcon />} onClick={onAddRow}>
                                Zuordnung hinzufuegen
                            </Button>
                        </Stack>
                    )}
                </Stack>
            )}
        </DialogContent>
        <DialogActions>
            <Button onClick={onClose} disabled={assigning}>
                Abbrechen
            </Button>
            <Button variant="contained" onClick={onAssign} disabled={assigning}>
                {assigning ? 'Zuordnung laeuft...' : 'Zuordnen'}
            </Button>
        </DialogActions>
    </Dialog>
);

export default BankAssignDialog;
